import { Box, Paper, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { addDays, isAfter, isBefore, isValid, parse, subDays } from 'date-fns';
import { List, Map } from 'immutable';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import {
  fetchRelayControllerStatusForUnit,
  getRelayControllerStatusToUnitMapping,
} from '@sophocles/entities/RelayControllerStatus';
import {
  fetchRelayControllerStatusHistoriesForUnitWithRange,
  getLimitedRelayControllerStatusHistoryForSelectedSite,
} from '@sophocles/entities/RelayControllerStatusHistory';
import { selectSiteFeatureAssociationByKey } from '@sophocles/features/featureFlags/slice';
import { selectLightFixturesAsImmutableMap } from '@sophocles/features/reference';
import { REV, SMARTLINK_MODEL, getRevVersion } from '@sophocles/utils';
import { formatDateForSubmission, getDateTimeFormat } from '@sophocles/utils/formatUtils';
import { getMeasurementTypeLabel } from '@sophocles/utils/measurementUtils';
import { determineSupportsVantageSensorConfig } from '@sophocles/utils/version';
import ExternalPowerReadings from '@sophocles/views/sites/components/ExternalPowerReadings';
import MeasurementChart from '@sophocles/views/sites/components/MeasurementChart';
import MeasurementDataTable from '@sophocles/views/sites/components/MeasurementDataTable';
import PowerMenu from '@sophocles/views/sites/components/PowerMenu';
import RelayInfoCard from '@sophocles/views/sites/components/RelayInfoCard';
import { buildRelayDisplayData } from '@sophocles/views/sites/components/relayUtils';
import SensorDataGrid from '@sophocles/views/sites/components/SensorDataGrid';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

function PowerDataView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useUser();
  const { selectedStructure: site, customer, loading: dashboardLoading } = useSiteDashboardLayoutContext();

  const [loading, setLoading] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const initialFromRaw = queryParams.get('from');
  const initialToRaw = queryParams.get('to');

  // Capture whether URL had date params on initial mount (before effects write to URL)
  const [hadInitialDateParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('from') || params.has('to');
  });
  const initialFrom = initialFromRaw ? parse(initialFromRaw, 'yyyy-MM-dd', new Date()) : subDays(new Date(), 30);
  const initialTo = initialToRaw ? parse(initialToRaw, 'yyyy-MM-dd', new Date()) : addDays(new Date(), 1);
  const [from, setFrom] = useState(isValid(initialFrom) ? initialFrom : subDays(new Date(), 30));
  const [to, setTo] = useState(isValid(initialTo) ? initialTo : addDays(new Date(), 1));

  const displayUnit = 'power'; // Legacy devices always display in watts (no user toggle)

  const externalPowerMeterFeature = useSelector(
    selectSiteFeatureAssociationByKey(site.get('id', null), 'EXTERNAL_POWER_METER'),
  );
  const hasExternalPowerMeter = externalPowerMeterFeature?.is_enabled ?? false;
  const fixtures = useSelector(selectLightFixturesAsImmutableMap);

  const statusHistories = useSelector(getLimitedRelayControllerStatusHistoryForSelectedSite);
  const statusMap = useSelector(getRelayControllerStatusToUnitMapping);

  const timeZone = site.get('time_zone', null);
  const unitId = site.getIn(['relay_controller', 'id'], null);
  const unit = site.get('relay_controller', Map());
  const modelId = site.getIn(['relay_controller', 'model', 'id'], null);
  const version = unit?.getIn(['firmware_version', 'version'], null);
  const supportsSensorConfig = determineSupportsVantageSensorConfig(version);
  const measuresPower = site.getIn(['relay_controller', 'measures_power_consumption_in_watts'], false);
  const measuresCurrent = site.getIn(['relay_controller', 'model', 'measures_current_in_amps'], false);
  const numberOfRelays = site.getIn(['relay_controller', 'number_of_relays'], 2);

  const isVantage = modelId == SMARTLINK_MODEL.VANTAGE;
  const isDC = modelId == SMARTLINK_MODEL.SL_DC_WITHOUT_DOW || modelId == SMARTLINK_MODEL.SL_DC_WITH_DOW;
  const is2PDC = modelId == SMARTLINK_MODEL.SL_TWO_PORT_DC;
  const is302 = modelId == SMARTLINK_MODEL.SL_302;
  const rev = getRevVersion({ statusMap, unitId });
  const hideVoltage = isDC || isVantage;
  const hideVoltageEdit = is302;

  // Relay pairing and load info
  const isBillboard = customer?.get('is_billboard', true) ?? true;
  const relays = site.getIn(['relay_controller', 'relays'], List());
  const { relayInfo, displayRelays, expectedReadings } = buildRelayDisplayData(relays, numberOfRelays, t);

  // Build measurement groups for toolbar tab toggles (matching MeasurementDataView pattern)
  const measurementGroups = useMemo(() => {
    const groups = {};

    if (!isVantage) {
      groups.peak = {
        baseType: 'peak',
        displayType: 'peak',
        types: ['peak_power'],
        hasCurrentType: false,
        hasPowerType: true,
      };
    }

    groups.live = {
      baseType: 'live',
      displayType: 'live',
      types: ['live_power'],
      hasCurrentType: false,
      hasPowerType: true,
    };

    if (hasExternalPowerMeter) {
      groups.external = {
        baseType: 'external',
        displayType: 'external',
        types: ['external'],
        hasCurrentType: false,
        hasPowerType: false,
      };
    }

    if (isVantage && supportsSensorConfig) {
      groups.temperature = {
        baseType: 'temperature',
        displayType: 'temperature',
        types: ['temperature'],
        hasCurrentType: false,
        hasPowerType: false,
      };
    }

    return groups;
  }, [isVantage, hasExternalPowerMeter, supportsSensorConfig]);

  const [tab, setTab] = useState(() => {
    if (hasExternalPowerMeter) return 'external';
    if (isVantage || is2PDC) return 'live';
    return 'peak';
  });
  const [groupMode, setGroupMode] = useState('all');

  // Set default groupMode per tab: "byDay" for peak, "all" for others
  useEffect(() => {
    if (tab) {
      setGroupMode(tab === 'peak' ? 'byDay' : 'all');
    }
  }, [tab]);

  const isPowerTab = tab === 'live' || tab === 'peak';

  // Determine the Redux data field based on tab and device capabilities
  const dataType = useMemo(() => {
    let string = 'relay_';
    string += tab === 'live' ? 'instantaneous_' : 'max_';
    string += measuresPower ? 'power_watts' : measuresCurrent ? 'currents_amps' : 'currents_arbitrary_units';
    return string;
  }, [tab, measuresPower, measuresCurrent]);

  const isCurrentData = dataType.includes('currents_amps');
  const isArbitraryData = dataType.includes('currents_arbitrary_units');

  // Process status histories into individual measurement records (MeasurementDataView format)
  const processedMeasurements = useMemo(() => {
    if (!isPowerTab) return [];

    const measurements = [];

    statusHistories
      .filter(data => {
        const m = data.get('moment');
        const notNull = data.get(dataType, null) != null;
        return notNull && isAfter(m, from) && isBefore(m, to) && _.some(data.get(dataType, [null]), d => d !== null);
      })
      .forEach(data => {
        const rawData = data.toJS();
        const utcDate = new Date(rawData.moment);
        const dataArray = rawData[dataType];

        for (let i = 1; i <= numberOfRelays; i++) {
          const rawValue = _.get(dataArray, i - 1, null);
          if (rawValue == null) continue;

          const relayVoltage = relayInfo[i]?.voltage ?? 120;

          // Convert amps to watts when data source is current; arbitrary units pass through as-is.
          // For watts data: firmware stores watts assuming 120V, rescale to actual relay voltage.
          const powerValue = isCurrentData
            ? rawValue * relayVoltage
            : isArbitraryData
              ? rawValue
              : rawValue * (relayVoltage / 120.0);
          const measurementUnit = isArbitraryData ? '' : 'watts';

          const relay = relays.find(r => r.get('relay_number') === i);
          const relayFallback = t('sites.relayNumber', 'Relay {{number}}', { number: i });
          const relayName = relay ? relay.get('name', relayFallback) : relayFallback;

          measurements.push({
            id: `${rawData.id || utcDate.getTime()}-relay-${i}`,
            timestamp: utcDate,
            datetime_timestamp: utcDate.toISOString(),
            short_timestamp: utcDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }),
            type: tab === 'live' ? 'live_power' : 'peak_power',
            value: powerValue,
            unit: measurementUnit,
            discriminator: String(i),
            relay: {
              relay_number: i,
              voltage: relayVoltage,
              name: relayName,
            },
            // DC/Vantage unit-specific fields (per status record, not per relay)
            battery_voltage: rawData.battery_voltage,
            thermistor_temperature: rawData.thermistor_temperature,
            input_voltage_1: rawData.input_voltage_1,
            input_voltage_2: rawData.input_voltage_2,
            cpu_input_voltage: rawData.cpu_input_voltage,
          });
        }
      });

    return measurements;
  }, [statusHistories, dataType, from, to, numberOfRelays, relayInfo, relays, tab, isCurrentData, isPowerTab]);

  // Group measurements by timestamp for table display
  const groupedDataList = useMemo(() => {
    const grouped = {};

    processedMeasurements.forEach(measurement => {
      const timestampKey = measurement.datetime_timestamp;

      if (!grouped[timestampKey]) {
        grouped[timestampKey] = {
          timestamp: measurement.timestamp,
          datetime_timestamp: measurement.datetime_timestamp,
          relayMeasurements: {},
          sensorMeasurements: {},
          otherMeasurements: {},
        };
      }

      if (measurement.relay) {
        grouped[timestampKey].relayMeasurements[measurement.relay.relay_number] = {
          value: typeof measurement.value === 'number' ? measurement.value : parseFloat(measurement.value) || 0,
          unit: measurement.unit,
          relay: measurement.relay,
        };
      }

      // Carry DC/Vantage unit-specific fields (same for all relays in a timestamp)
      if (measurement.battery_voltage != null) grouped[timestampKey].battery_voltage = measurement.battery_voltage;
      if (measurement.thermistor_temperature != null)
        grouped[timestampKey].thermistor_temperature = measurement.thermistor_temperature;
      if (measurement.input_voltage_1 != null) grouped[timestampKey].input_voltage_1 = measurement.input_voltage_1;
      if (measurement.input_voltage_2 != null) grouped[timestampKey].input_voltage_2 = measurement.input_voltage_2;
      if (measurement.cpu_input_voltage != null)
        grouped[timestampKey].cpu_input_voltage = measurement.cpu_input_voltage;
    });

    return Object.values(grouped).sort((a, b) => b.timestamp - a.timestamp);
  }, [processedMeasurements]);

  // Sorted chronologically for chart
  const dataList = useMemo(() => {
    return [...processedMeasurements].sort((a, b) => a.timestamp - b.timestamp);
  }, [processedMeasurements]);

  // Update query params when 'from' or 'to' changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('from', formatDateForSubmission(from));
    params.set('to', formatDateForSubmission(to));
    const next = `?${params.toString()}`;
    if (next === location.search) return;
    navigate(next, { replace: true });
  }, [from, to, navigate, location.search]);

  useEffect(() => {
    if (hasExternalPowerMeter && tab !== 'external') {
      setTab('external');
    }
  }, [hasExternalPowerMeter]);

  useEffect(() => {
    if (unitId) {
      setLoading(true);
      const fromFormatted = formatDateForSubmission(from);
      const toFormatted = formatDateForSubmission(to);
      Promise.all([
        dispatch(fetchRelayControllerStatusHistoriesForUnitWithRange(unitId, fromFormatted, toFormatted)),
        dispatch(fetchRelayControllerStatusForUnit(unitId)),
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [dispatch, from, to, unitId]);

  const handleTabChange = useCallback((_event, newValue) => {
    setTab(newValue);
  }, []);

  const handleDateRangeChange = useCallback((start, end) => {
    if (start) setFrom(start);
    if (end) setTo(end);
  }, []);

  const dateTimeFormat = getDateTimeFormat(user);

  const dateRangeProps = {
    startDate: from,
    endDate: to,
    onDateRangeChange: handleDateRangeChange,
    dateRangeLoading: loading,
    dateRangeExclude: ['last1h', 'last24h'],
    defaultDatePreset: hadInitialDateParams ? 'custom' : 'last30d',
  };

  const tableCommonProps = {
    tab,
    onTabChange: handleTabChange,
    measurementGroups,
    ...dateRangeProps,
  };

  // Styling for standalone toolbar on non-power tabs (matches MeasurementDataTable toolbar toggles)
  const chipBorder = alpha(theme.palette.text.primary, 0.23);
  const toggleGroupSx = {
    height: 24,
    '& .MuiToggleButtonGroup-firstButton': { borderRadius: 'var(--radius-lg) 0 0 var(--radius-lg)' },
    '& .MuiToggleButtonGroup-lastButton': { borderRadius: '0 var(--radius-lg) var(--radius-lg) 0' },
    '& .MuiToggleButtonGroup-middleButton': { borderRadius: 0 },
  };
  const toggleButtonSx = {
    px: 1,
    fontSize: '0.8125rem',
    textTransform: 'none',
    lineHeight: 1,
    color: 'text.primary',
    border: '1px solid',
    borderColor: chipBorder,
  };

  const sortedGroupKeys = useMemo(() => {
    const order = ['peak', 'live', 'average'];
    return Object.keys(measurementGroups).sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [measurementGroups]);

  if (dashboardLoading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ pb: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <PowerMenu selectedPage="power" site={site.toJS()} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {loading && !statusHistories.size ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 'var(--radius-lg)' }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 'var(--radius-lg)' }} />
          </Box>
        ) : isPowerTab && measurementGroups[tab] ? (
          <>
            {/* Relay Load Info Cards */}
            {displayRelays.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                {displayRelays.map(dr => (
                  <RelayInfoCard
                    key={dr.key}
                    dr={dr}
                    relayInfo={relayInfo}
                    expectedReadings={expectedReadings}
                    dispatch={dispatch}
                    siteId={site.get('id')}
                    fixtures={fixtures}
                    isBillboard={isBillboard}
                    hideVoltage={hideVoltage}
                    hideVoltageEdit={hideVoltageEdit}
                  />
                ))}
              </Box>
            )}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 'var(--radius-lg)' }}>
              <MeasurementChart
                key={`chart-${tab}`}
                list={dataList}
                measurementType={tab}
                availableTypes={[]}
                expectedReadings={isBillboard ? expectedReadings : {}}
                displayRelays={displayRelays}
                relayInfo={relayInfo}
                displayUnit={displayUnit}
                groupMode={groupMode}
                timeZone={timeZone}
              />
            </Paper>

            <MeasurementDataTable
              key={`table-${tab}`}
              dateTimeFormat={dateTimeFormat}
              data={groupedDataList}
              relays={relays}
              relayInfo={relayInfo}
              displayRelays={displayRelays}
              expectedReadings={isBillboard ? expectedReadings : {}}
              displayUnit={displayUnit}
              timeZone={timeZone}
              groupMode={groupMode}
              isDC={isDC || is2PDC}
              rev={rev}
              isVantage={isVantage}
              onGroupModeChange={setGroupMode}
              {...tableCommonProps}
            />
          </>
        ) : !isPowerTab && measurementGroups[tab] ? (
          <>
            {/* Standalone toolbar for non-power tabs (external, temperature) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              {sortedGroupKeys.length > 1 && (
                <ToggleButtonGroup
                  value={tab}
                  exclusive
                  onChange={(_e, val) => val && handleTabChange(null, val)}
                  size="small"
                  sx={toggleGroupSx}
                >
                  {sortedGroupKeys.map(groupKey => (
                    <ToggleButton key={groupKey} value={groupKey} disabled={loading} sx={toggleButtonSx}>
                      {getMeasurementTypeLabel(measurementGroups[groupKey]?.displayType, t)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              <TimeRangeSelector
                startDate={from}
                endDate={to}
                onChange={handleDateRangeChange}
                disabled={loading}
                size="sm"
                exclude={['last1h', 'last24h']}
                defaultPreset={hadInitialDateParams ? 'custom' : 'last30d'}
              />
            </Box>

            {tab === 'external' ? (
              <ExternalPowerReadings site={site} startDate={from} endDate={to} />
            ) : tab === 'temperature' ? (
              <SensorDataGrid relayControllerId={unitId} startDate={from} endDate={to} />
            ) : null}
          </>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 'var(--radius-lg)', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {t('measurements.noMeasurementsAvailable')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('measurements.noMeasurementsInRange')}
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default PowerDataView;
